import { Component, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NotificationService } from 'src/app/Service/notification.service';
import { ActivatedRoute, Router } from '@angular/router';
import { GurdServiceService } from 'src/app/Service/gurd-service.service';
import { OtherService } from 'src/app/Service/other.service';
import axios from 'src/app/Service/axios';
import * as L from 'leaflet';
import { KpiSummary, Vehicle, BatteryStats } from './model/telemetry-dashboard.model';

@Component({
  selector: 'app-telemetry-dashboard',
  templateUrl: './telemetry-dashboard.component.html',
  styleUrls: ['./telemetry-dashboard.component.css']
})

export class TelemetryDashboardComponent implements OnInit, AfterViewInit {

  mdl_Request: FormGroup = this.FormBuilder.group({
    iSystemUser: this.gurdService.decrypt(localStorage.getItem('ID')!),
    strSession: this.gurdService.decrypt(localStorage.getItem('Session')!),
  });

  isLoading = true;
  lastUpdate = '--:--:--';

  kpi: KpiSummary | null = null;
  vehicles: Vehicle[] = [];
  batteryStats: BatteryStats | null = null;
  attentionVehicles: Vehicle[] = [];

  searchTerm = '';
  tableFilter: string = 'all';
  mapFilter: string = 'all';

  private fleetMap: L.Map | null = null;
  private mapMarkers: L.Marker[] = [];
  private mapInitialized = false;

  readonly statusMeta: Record<string, { title: string; color: string; className: string }> = {
    moving: { title: 'در حرکت', color: '#28d17c', className: 'status-moving' },
    charging: { title: 'در حال شارژ', color: '#3d9cff', className: 'status-charging' },
    ready: { title: 'آماده', color: '#26c6da', className: 'status-ready' },
    off: { title: 'خاموش', color: '#8d9aaa', className: 'status-off' },
    warning: { title: 'نیازمند توجه', color: '#ff5364', className: 'status-warning' },
  };

  constructor(
    private _tooltip: NotificationService,
    private FormBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private gurdService: GurdServiceService,
    private _otherService: OtherService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.getDashboardData();
  }

  ngAfterViewInit(): void {
    if (this.vehicles.length) {
      this.scheduleMapRefresh();
    }
  }

  async getDashboardData() {
    this.isLoading = true;
    try {
      this._otherService.Showspinner();
      const raw = (await axios.post(`CAN_FleetDashboard`, this._otherService.stringNumberToNumber(this.mdl_Request.value))).data;

      let response: any;
      try {
        response = typeof raw === 'string' ? JSON.parse(raw.toString()) : raw;

        if (typeof response === 'string') response = JSON.parse(response);
        if (response && typeof response.data === 'string') {
          try { response = JSON.parse(response.data); } catch {}
        }
      } catch (e) {
        response = raw;
      }

      this._otherService.nonespinner();

      if (response?.ISSessionExpired === true) {
        this._tooltip.showError('نشست کاربری شما منقضی شده است');
        this._otherService.removeLocalStorage();
        this.router.navigate(['login'], { queryParams: { error: 'CAN_FleetDashboard' } });
        return;
      }
      if (response?.iResult === 0) {
        this._tooltip.showError(response.strError || 'خطا در دریافت اطلاعات');
        this.isLoading = false;
        return;
      }

      const t = response?.Table ?? response?.table ?? [];
      const t1 = response?.Table1 ?? response?.table1 ?? [];
      const t2 = response?.Table2 ?? response?.table2 ?? [];
      const t3 = response?.Table3 ?? response?.table3 ?? [];

      this.kpi = t.length ? t[0] as KpiSummary : null;
      this.vehicles = Array.isArray(t1) ? t1 as Vehicle[] : [];
      this.batteryStats = t2.length ? t2[0] as BatteryStats : null;
      this.attentionVehicles = Array.isArray(t3) ? t3 as Vehicle[] : [];

      if (!this.kpi && this.vehicles.length) {
        this.kpi = this.computeKpiFallback();
      }

      if (!this.batteryStats && this.vehicles.length) {
        this.batteryStats = this.computeBatteryStatsFallback();
      }

      if (!this.attentionVehicles.length) {
        this.attentionVehicles = this.vehicles.filter(v => v.NeedsAttention);
      }

      this.lastUpdate = new Date().toLocaleTimeString('fa-IR');
      this.isLoading = false;
      this.cdr.detectChanges();

      this.scheduleMapRefresh();

    } catch (err: any) {
      this._otherService.nonespinner();
      this.isLoading = false;
      console.error('[TelemetryDashboard] getDashboardData error', err);
      this._tooltip.showError('خطا در دریافت اطلاعات داشبورد');
    }
  }

  private computeKpiFallback(): KpiSummary {
    const total = this.vehicles.length;
    const moving = this.vehicles.filter(v => this.normalizeStatus(v) === 'moving').length;
    const charging = this.vehicles.filter(v => this.normalizeStatus(v) === 'charging').length;
    const ready = this.vehicles.filter(v => this.normalizeStatus(v) === 'ready').length;
    const attention = this.vehicles.filter(v => v.NeedsAttention).length;
    const avgSOC = total ? this.vehicles.reduce((s, v) => s + (v.BatterySOC ?? 0), 0) / total : 0;
    return { TotalVehicles: total, MovingVehicles: moving, ChargingVehicles: charging, ReadyVehicles: ready, AverageSOC: Number(avgSOC.toFixed(2)), AttentionVehicles: attention };
  }

  private computeBatteryStatsFallback(): BatteryStats {
    const total = this.vehicles.length || 1;
    const avgSOC = this.vehicles.reduce((s, v) => s + (v.BatterySOC ?? 0), 0) / total;
    const avgSOH = this.vehicles.reduce((s, v) => s + (v.BatterySOH ?? 0), 0) / total;
    const low = this.vehicles.filter(v => (v.BatterySOC ?? 100) < 20).length;
    const socBuckets = [0, 0, 0, 0, 0];
    this.vehicles.forEach(v => {
      const soc = v.BatterySOC ?? 0;
      if (soc < 20) socBuckets[0]++;
      else if (soc < 40) socBuckets[1]++;
      else if (soc < 60) socBuckets[2]++;
      else if (soc < 80) socBuckets[3]++;
      else socBuckets[4]++;
    });
    return {
      AverageSOC: Number(avgSOC.toFixed(2)),
      AverageSOH: Number(avgSOH.toFixed(2)),
      LowSOCVehicles: low,
      BatteryReviewVehicles: this.vehicles.filter(v => (v.BatterySOH ?? 100) < 80).length,
      SOC_0_20: socBuckets[0], SOC_20_40: socBuckets[1], SOC_40_60: socBuckets[2], SOC_60_80: socBuckets[3], SOC_80_100: socBuckets[4]
    };
  }

  normalizeStatus(v: Vehicle): string {
    const code = (v.VehicleStatusCode || '').toLowerCase().trim();
    if (['moving', 'drive', 'run'].includes(code)) return 'moving';
    if (['charging', 'charge'].includes(code)) return 'charging';
    if (['ready', 'idle', 'standby'].includes(code)) return 'ready';
    if (['off', 'stop', 'offline'].includes(code)) return 'off';
    return 'off';
  }

  getStatusInfo(v: Vehicle) {
    if (v.NeedsAttention) return this.statusMeta['warning'];
    const n = this.normalizeStatus(v);
    return this.statusMeta[n] || this.statusMeta['off'];
  }

  getStatusClass(v: Vehicle): string {
    return this.getStatusInfo(v).className;
  }

  getStatusTitle(v: Vehicle): string {
    return this.getStatusInfo(v).title;
  }

  getSohClass(soh: number | null): string {
    if (soh == null) return 'health-good';
    if (soh < 80) return 'health-danger';
    if (soh < 90) return 'health-warning';
    return 'health-good';
  }

  get filteredVehicles(): Vehicle[] {
    let result = [...this.vehicles];
    if (this.tableFilter !== 'all') {
      if (this.tableFilter === 'warning') {
        result = result.filter(v => v.NeedsAttention);
      } else {
        result = result.filter(v => this.normalizeStatus(v) === this.tableFilter);
      }
    }
    const q = this.searchTerm.trim().toLowerCase();
    if (q) {
      result = result.filter(v =>
        (v.strFullName || '').toLowerCase().includes(q) ||
        (v.strVehicleNo || '').toLowerCase().includes(q) ||
        (v.strUnitID || '').toLowerCase().includes(q) ||
        (v.strVIN || '').toLowerCase().includes(q) ||
        (v.strMobile || '').toLowerCase().includes(q)
      );
    }
    return result;
  }

  setTableFilter(filter: string) {
    this.tableFilter = filter;
  }

  setMapFilter(filter: string) {
    this.mapFilter = filter;
    this.renderMapMarkers();
  }

  viewDetails(vehicle: Vehicle): void {
    const url = this.router.createUrlTree(['/carInfo', String(vehicle.strUnitID).trim()]).toString();
    window.open(url, '_blank');
  }

  private scheduleMapRefresh(retries = 8): void {
    setTimeout(() => {
      const el = document.getElementById('fleetMap');
      if (!el) {
        if (retries > 0) this.scheduleMapRefresh(retries - 1);
        return;
      }

      if (this.fleetMap) {
        const container: any = (this.fleetMap as any)._container;
        if (container !== el) {
          try { this.fleetMap.remove(); } catch {}
          this.fleetMap = null;
          this.mapMarkers = [];
          this.mapInitialized = false;
        }
      }
      if (!this.fleetMap || !this.mapInitialized) {
        this.initMap();
      } else {
        this.renderMapMarkers();
        setTimeout(() => { try { this.fleetMap?.invalidateSize(); } catch {} }, 100);
      }
    }, 250);
  }

  private initMap(): void {
    const el = document.getElementById('fleetMap');
    if (!el) return;

    if (this.fleetMap) {
      try { this.fleetMap.remove(); } catch {}
      this.fleetMap = null;
      this.mapMarkers = [];
    }

    const elAny = el as any;
    if (elAny._leaflet_id && !this.fleetMap) {

      try { el.innerHTML = ''; } catch {}
      try { delete elAny._leaflet_id; } catch { elAny._leaflet_id = null; }
    }

    let lat = 35.73, lng = 51.42;
    if (this.vehicles.length) {
      const valid = this.vehicles.filter(v => v.dLatitude && v.dLongitude);
      if (valid.length) {
        lat = valid.reduce((s, v) => s + v.dLatitude, 0) / valid.length;
        lng = valid.reduce((s, v) => s + v.dLongitude, 0) / valid.length;
      }
    }
    this.fleetMap = L.map('fleetMap', { zoomControl: true }).setView([lat, lng], 12);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap'
    }).addTo(this.fleetMap);
    this.mapInitialized = true;
    this.renderMapMarkers();
    setTimeout(() => this.fleetMap?.invalidateSize(), 300);
  }

  private createVehicleIcon(v: Vehicle): L.DivIcon {
    const info = this.getStatusInfo(v);
    return L.divIcon({
      className: '',
      html: `<div class="vehicle-marker" style="background:${info.color}"><span>⚡</span></div>`,
      iconSize: [30, 30],
      iconAnchor: [15, 30],
      popupAnchor: [0, -30]
    });
  }

  private renderMapMarkers(): void {
    if (!this.fleetMap) return;
    this.mapMarkers.forEach(m => this.fleetMap!.removeLayer(m));
    this.mapMarkers = [];

    const list = this.vehicles.filter(v => {
      if (this.mapFilter === 'all') return true;
      if (this.mapFilter === 'warning') return !!v.NeedsAttention;
      return this.normalizeStatus(v) === this.mapFilter;
    });

    list.forEach(v => {
      if (!v.dLatitude || !v.dLongitude) return;
      const marker = L.marker([v.dLatitude, v.dLongitude], { icon: this.createVehicleIcon(v) });
      const soc = v.BatterySOC != null ? v.BatterySOC.toFixed(1) + '%' : '—';
      const soh = v.BatterySOH != null ? v.BatterySOH.toFixed(1) + '%' : '—';
      const speed = v.VehicleSpeed != null ? v.VehicleSpeed + ' km/h' : '—';
      marker.bindPopup(`
        <div class="vehicle-popup-title">${v.strFullName || v.strVehicleNo}</div>
        <div class="popup-row"><span>وضعیت</span><strong>${this.getStatusTitle(v)}</strong></div>
        <div class="popup-row"><span>سرعت</span><strong>${speed}</strong></div>
        <div class="popup-row"><span>SOC</span><strong>${soc}</strong></div>
        <div class="popup-row"><span>SOH</span><strong>${soh}</strong></div>
        <div class="popup-row"><span>پلاک</span><strong>${v.strVehicleNo || '—'}</strong></div>
        <button class="popup-button" data-unit="${v.strUnitID}">مشاهده جزئیات خودرو</button>
      `);

      marker.on('popupopen', () => {
        setTimeout(() => {
          const btn = document.querySelector(`.popup-button[data-unit="${v.strUnitID}"]`);
          if (btn) btn.addEventListener('click', () => this.viewDetails(v));
        }, 50);
      });
      marker.addTo(this.fleetMap!);
      this.mapMarkers.push(marker);
    });
  }

  get socDistribution(): number[] {
    if (this.batteryStats) {
      return [this.batteryStats.SOC_0_20, this.batteryStats.SOC_20_40, this.batteryStats.SOC_40_60, this.batteryStats.SOC_60_80, this.batteryStats.SOC_80_100];
    }

    const arr = [0, 0, 0, 0, 0];
    this.vehicles.forEach(v => {
      const soc = v.BatterySOC ?? 0;
      if (soc < 20) arr[0]++; else if (soc < 40) arr[1]++; else if (soc < 60) arr[2]++; else if (soc < 80) arr[3]++; else arr[4]++;
    });
    return arr;
  }

  get maxSocBucket(): number {
    return Math.max(...this.socDistribution, 1);
  }

  get avgSpeed(): number {
    const valid = this.vehicles.filter(v => v.VehicleSpeed != null) as Vehicle[];
    if (!valid.length) return 0;
    return valid.reduce((s, v) => s + (v.VehicleSpeed as number), 0) / valid.length;
  }

  get mapVehiclesCount(): number {
    if (this.mapFilter === 'all') return this.vehicles.length;
    if (this.mapFilter === 'warning') return this.vehicles.filter(v => v.NeedsAttention).length;
    return this.vehicles.filter(v => this.normalizeStatus(v) === this.mapFilter).length;
  }

  getAttentionCount(type: 'battery' | 'thermal'): number {
    if (type === 'battery') return this.vehicles.filter(v => (v.BatterySOC != null && v.BatterySOC < 20) || (v.BatterySOH != null && v.BatterySOH < 80)).length;
    if (type === 'thermal') return this.vehicles.filter(v => (v.MaxCellTemperature ?? 0) > 50).length;
    return 0;
  }

  trackByUnitId(_: number, item: Vehicle): string { return item.strUnitID; }
}
